/* First-open language onboarding (owner 2026-10-24): drops down from the top
 * the moment the app opens, page blurred behind it. It re-asks on EVERY open
 * until the driver actually PICKS a language — a stray tap on the blurred
 * backdrop must never kill it forever (owner report: prompt never dropped
 * down after an accidental tap dismissed an earlier version). English is one
 * of the chips, so English drivers make one tap once and never see it again.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { Globe } from "lucide-react";
import { applyLang, getLang, LANGS } from "@/lib/i18n";

export default function LanguageOnboarding() {
    const [show, setShow] = useState(() => {
        try {
            return !localStorage.getItem("pcf_lang_picked");
        } catch {
            return false;
        }
    });
    const [busy, setBusy] = useState(false);

    if (!show) return null;

    const choose = (code) => {
        if (busy) return;
        setBusy(true);
        try {
            localStorage.setItem("pcf_lang_picked", "1");
        } catch {}
        // Dismiss immediately — translation continues in the background and
        // the page flips progressively as batches land (a first-ever language
        // can take 30s+; the driver shouldn't stare at a blocking card)
        if (code !== "en") applyLang(code);
        setShow(false);
    };

    return (
        <div data-testid="language-onboarding" className="fixed inset-0 z-[160] bg-[#0E0D0C]/60 backdrop-blur-md">
            <motion.div
                initial={{ y: "-120%" }}
                animate={{ y: 0 }}
                transition={{ type: "spring", stiffness: 170, damping: 22 }}
                className="relative mx-auto mt-4 w-[min(92vw,28rem)] border-2 border-[#E5A93C] bg-[#161413] p-6 shadow-[6px_6px_0px_#D32F2F]"
            >
                <button
                    data-testid="onboarding-dismiss"
                    onClick={() => setShow(false)}
                    title="Not now — it will ask again next time"
                    className="btn-press font-mono2 absolute right-3 top-3 border-2 border-[#8C8377]/50 px-2 py-1 text-[10px] font-bold tracking-widest text-[#8C8377] hover:border-[#F6F2E8] hover:text-[#F6F2E8]"
                >
                    ✕
                </button>
                <div className="flex items-center gap-3">
                    <Globe size={26} strokeWidth={2.2} className="shrink-0 text-[#E5A93C]" />
                    <div className="font-display text-2xl tracking-wide text-[#F6F2E8]">PICK YOUR LANGUAGE</div>
                </div>
                <p className="font-mono2 mt-1 text-[10px] tracking-[0.2em] text-[#8C8377]">
                    EVERY SCREEN + TURN-BY-TURN VOICE
                </p>
                <div data-testid="onboarding-language-list" data-no-translate className="mt-4 grid grid-cols-2 gap-2">
                    {LANGS.map((l) => (
                        <button
                            key={l.code}
                            data-testid={`onboarding-lang-${l.code}`}
                            disabled={busy}
                            onClick={() => choose(l.code)}
                            className={`btn-press font-mono2 border-2 px-3 py-3 text-center text-xs font-bold tracking-[0.15em] disabled:opacity-50 ${
                                l.code === getLang()
                                    ? "border-[#E5A93C] bg-[#E5A93C] text-[#161413]"
                                    : "border-[#E5A93C]/40 bg-[#24211E] text-[#F6F2E8] hover:border-[#E5A93C]"
                            }`}
                        >
                            {l.name}
                        </button>
                    ))}
                </div>
            </motion.div>
        </div>
    );
}
